import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { QueryPatternUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { EvaluationPathType } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { QueryPatternSource } from "../../../../api/graphql";
import { InputNumber } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type QueryPatternUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  pattern: string;
  patternSelectedValue?: { displayId: string; id: string } | null;
  onPatternChange?: (value: string) => void;
  patternError?: string;
  pathTypeOptions: { id: string; displayId: string }[];
  pathTypeError?: string;
  pathType: EvaluationPathType;
  onPathTypeChange?: (value: EvaluationPathType) => void;
  variables: string;
  variablesSelectedValue?: { displayId: string; id: string } | null;
  onVariablesChange?: (value: string) => void;
  variablesError?: string;
  toolTemplate: string;
  toolTemplateSelectedValue?: { displayId: string; id: string } | null;
  onToolTemplateChange?: (value: string) => void;
  toolTemplateError?: string;
  argsTemplate: string;
  argsTemplateSelectedValue?: { displayId: string; id: string } | null;
  onArgsTemplateChange?: (value: string) => void;
  argsTemplateError?: string;
  variations: string;
  variationsSelectedValue?: { displayId: string; id: string } | null;
  onVariationsChange?: (value: string) => void;
  variationsError?: string;
  exampleInstantiation: string;
  exampleInstantiationSelectedValue?: { displayId: string; id: string } | null;
  onExampleInstantiationChange?: (value: string) => void;
  exampleInstantiationError?: string;
  sourceOptions: { id: string; displayId: string }[];
  sourceError?: string;
  source: QueryPatternSource;
  onSourceChange?: (value: QueryPatternSource) => void;
  qualityScore: number;
  qualityScoreSelectedValue?: { displayId: string; id: string } | null;
  onQualityScoreChange?: (value: number) => void;
  qualityScoreError?: string;
  usageCount: number;
  usageCountSelectedValue?: { displayId: string; id: string } | null;
  onUsageCountChange?: (value: number) => void;
  usageCountError?: string;
  isActive: boolean;
  isActiveSelectedValue?: { displayId: string; id: string } | null;
  onIsActiveChange?: (value: boolean) => void;
  isActiveError?: string;
  notes: string;
  notesSelectedValue?: { displayId: string; id: string } | null;
  onNotesChange?: (value: string) => void;
  notesError?: string;
  queryPatternUpdateStore: QueryPatternUpdateStore;
};

export const QueryPatternUpdateForm: React.FC<QueryPatternUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      pattern = "",
      patternError,
      onPatternChange = () => {},
      pathTypeOptions = [],
      pathTypeError,
      pathType = "" as EvaluationPathType,
      onPathTypeChange = () => {},
      variables = "{}",
      variablesError,
      onVariablesChange = () => {},
      toolTemplate = "",
      toolTemplateError,
      onToolTemplateChange = () => {},
      argsTemplate = "{}",
      argsTemplateError,
      onArgsTemplateChange = () => {},
      variations = "{}",
      variationsError,
      onVariationsChange = () => {},
      exampleInstantiation = "",
      exampleInstantiationError,
      onExampleInstantiationChange = () => {},
      sourceOptions = [],
      sourceError,
      source = "" as QueryPatternSource,
      onSourceChange = () => {},
      qualityScore = 0,
      qualityScoreError,
      onQualityScoreChange = () => {},
      usageCount = 0,
      usageCountError,
      onUsageCountChange = () => {},
      isActive = false,
      isActiveError,
      onIsActiveChange = () => {},
      notes = "",
      notesError,
      onNotesChange = () => {},
      queryPatternUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="QueryPattern"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "pattern":
                    onPatternChange?.(valor);
                    break;
                  case "pathType":
                    onPathTypeChange?.(valor);
                    break;
                  case "variables":
                    onVariablesChange?.(valor);
                    break;
                  case "toolTemplate":
                    onToolTemplateChange?.(valor);
                    break;
                  case "argsTemplate":
                    onArgsTemplateChange?.(valor);
                    break;
                  case "variations":
                    onVariationsChange?.(valor);
                    break;
                  case "exampleInstantiation":
                    onExampleInstantiationChange?.(valor);
                    break;
                  case "source":
                    onSourceChange?.(valor);
                    break;
                  case "qualityScore":
                    onQualityScoreChange?.(valor);
                    break;
                  case "usageCount":
                    onUsageCountChange?.(valor);
                    break;
                  case "isActive":
                    onIsActiveChange?.(valor);
                    break;
                  case "notes":
                    onNotesChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={queryPatternUpdateStore.getCardErrors("null")}
              title={langStore.groupQueryPatternLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(queryPatternUpdateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    queryPatternUpdateStore.warnings["name"]?.message
                  }
                  label={langStore.QueryPatternNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.QueryPatternNameDescription}
                />
                <InputString
                  value={pattern}
                  error={Boolean(patternError)}
                  warning={
                    !patternError &&
                    Boolean(queryPatternUpdateStore.warnings["pattern"])
                  }
                  helperText={
                    patternError ||
                    queryPatternUpdateStore.warnings["pattern"]?.message
                  }
                  label={langStore.QueryPatternPatternLabel}
                  onChange={(value: string) => onPatternChange(value)}
                  description={langStore.QueryPatternPatternDescription}
                />
                <InputReference
                  id="pathType"
                  disabled={false}
                  error={Boolean(pathTypeError)}
                  helperText={
                    pathTypeError ||
                    queryPatternUpdateStore.warnings["pathType"]?.message
                  }
                  label={langStore.QueryPatternPathTypeLabel}
                  options={pathTypeOptions}
                  selectedValue={
                    pathType
                      ? {
                          id: pathType,
                          displayId:
                            pathTypeOptions.find(
                              (o: { id: string }) => o.id === pathType
                            )?.displayId || pathType,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onPathTypeChange(
                      (value?.id as EvaluationPathType) ||
                        ("" as EvaluationPathType)
                    )
                  }
                />
                <InputString
                  value={variables}
                  error={Boolean(variablesError)}
                  warning={
                    !variablesError &&
                    Boolean(queryPatternUpdateStore.warnings["variables"])
                  }
                  helperText={
                    variablesError ||
                    queryPatternUpdateStore.warnings["variables"]?.message
                  }
                  label={langStore.QueryPatternVariablesLabel}
                  onChange={(value: string) => onVariablesChange(value)}
                  description={langStore.QueryPatternVariablesDescription}
                />
                <InputString
                  value={toolTemplate}
                  error={Boolean(toolTemplateError)}
                  warning={
                    !toolTemplateError &&
                    Boolean(queryPatternUpdateStore.warnings["toolTemplate"])
                  }
                  helperText={
                    toolTemplateError ||
                    queryPatternUpdateStore.warnings["toolTemplate"]?.message
                  }
                  label={langStore.QueryPatternToolTemplateLabel}
                  onChange={(value: string) => onToolTemplateChange(value)}
                  description={langStore.QueryPatternToolTemplateDescription}
                />
                <InputString
                  value={argsTemplate}
                  error={Boolean(argsTemplateError)}
                  warning={
                    !argsTemplateError &&
                    Boolean(queryPatternUpdateStore.warnings["argsTemplate"])
                  }
                  helperText={
                    argsTemplateError ||
                    queryPatternUpdateStore.warnings["argsTemplate"]?.message
                  }
                  label={langStore.QueryPatternArgsTemplateLabel}
                  onChange={(value: string) => onArgsTemplateChange(value)}
                  description={langStore.QueryPatternArgsTemplateDescription}
                />
                <InputString
                  value={variations}
                  error={Boolean(variationsError)}
                  warning={
                    !variationsError &&
                    Boolean(queryPatternUpdateStore.warnings["variations"])
                  }
                  helperText={
                    variationsError ||
                    queryPatternUpdateStore.warnings["variations"]?.message
                  }
                  label={langStore.QueryPatternVariationsLabel}
                  onChange={(value: string) => onVariationsChange(value)}
                  description={langStore.QueryPatternVariationsDescription}
                />
                <InputString
                  value={exampleInstantiation}
                  error={Boolean(exampleInstantiationError)}
                  warning={
                    !exampleInstantiationError &&
                    Boolean(
                      queryPatternUpdateStore.warnings["exampleInstantiation"]
                    )
                  }
                  helperText={
                    exampleInstantiationError ||
                    queryPatternUpdateStore.warnings["exampleInstantiation"]
                      ?.message
                  }
                  label={langStore.QueryPatternExampleInstantiationLabel}
                  onChange={(value: string) =>
                    onExampleInstantiationChange(value)
                  }
                  description={
                    langStore.QueryPatternExampleInstantiationDescription
                  }
                />
                <InputReference
                  id="source"
                  disabled={false}
                  error={Boolean(sourceError)}
                  helperText={
                    sourceError ||
                    queryPatternUpdateStore.warnings["source"]?.message
                  }
                  label={langStore.QueryPatternSourceLabel}
                  options={sourceOptions}
                  selectedValue={
                    source
                      ? {
                          id: source,
                          displayId:
                            sourceOptions.find(
                              (o: { id: string }) => o.id === source
                            )?.displayId || source,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onSourceChange(
                      (value?.id as QueryPatternSource) ||
                        ("" as QueryPatternSource)
                    )
                  }
                />
                <InputNumber
                  value={qualityScore}
                  error={Boolean(qualityScoreError)}
                  warning={
                    !qualityScoreError &&
                    Boolean(queryPatternUpdateStore.warnings["qualityScore"])
                  }
                  helperText={
                    qualityScoreError ||
                    queryPatternUpdateStore.warnings["qualityScore"]?.message
                  }
                  label={langStore.QueryPatternQualityScoreLabel}
                  decimalScale={0}
                  description={langStore.QueryPatternQualityScoreDescription}
                  onChange={(value: number) => onQualityScoreChange(value)}
                />
                <InputNumber
                  value={usageCount}
                  error={Boolean(usageCountError)}
                  warning={
                    !usageCountError &&
                    Boolean(queryPatternUpdateStore.warnings["usageCount"])
                  }
                  helperText={
                    usageCountError ||
                    queryPatternUpdateStore.warnings["usageCount"]?.message
                  }
                  label={langStore.QueryPatternUsageCountLabel}
                  decimalScale={0}
                  onChange={(value: number) => onUsageCountChange(value)}
                />
                <InputBoolean
                  value={isActive}
                  error={Boolean(isActiveError)}
                  helperText={
                    isActiveError ||
                    queryPatternUpdateStore.warnings["isActive"]?.message
                  }
                  label={langStore.QueryPatternIsActiveLabel}
                  onChange={(value: boolean) => onIsActiveChange(value)}
                />
                <InputText
                  value={notes}
                  error={Boolean(notesError)}
                  warning={
                    !notesError &&
                    Boolean(queryPatternUpdateStore.warnings["notes"])
                  }
                  helperText={
                    notesError ||
                    queryPatternUpdateStore.warnings["notes"]?.message
                  }
                  label={langStore.QueryPatternNotesLabel}
                  onChange={(value: string) => onNotesChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
